import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CreditCard, Check, X, Loader } from 'lucide-react';
import { customerSubscriptionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function CustomerSubscriptionPage() {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Fetch subscription data
  const { data: subscriptionData, isLoading, error, refetch } = useQuery({
    queryKey: ['customer-subscription'],
    queryFn: () => customerSubscriptionsAPI.getSubscription(),
    retry: false,
    // Handle errors gracefully - show plans even if there's an error
    onError: (err) => {
      console.error('Subscription fetch error:', err);
      // Don't show error toast - we'll show placeholder plans instead
    }
  });

  // Use placeholder plans if API fails or returns no plans
  const defaultPlans = {
    basic: {
      name: 'Basic Package',
      price: 20.00,
      features: [
        'Access to all classes',
        'Standard features',
        'Email support'
      ],
      popular: false
    },
    standard: {
      name: 'Standard Package',
      price: 30.00,
      features: [
        'Access to all classes',
        'Enhanced features',
        'Priority support',
        'Early access to new content'
      ],
      popular: true
    },
    premium: {
      name: 'Premium Package',
      price: 50.00,
      features: [
        'Access to all classes',
        'All premium features',
        '24/7 priority support',
        'Exclusive content',
        'Personal consultation'
      ],
      popular: false
    }
  };

  const subscription = subscriptionData?.data?.customer;
  const plans = subscriptionData?.data?.plans || defaultPlans;

  // Create checkout session mutation
  const checkoutMutation = useMutation({
    mutationFn: (planType) => customerSubscriptionsAPI.createCheckoutSession({ planType }),
    onSuccess: (response) => {
      // Redirect to Stripe Checkout
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create checkout session');
    }
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: () => customerSubscriptionsAPI.cancelSubscription(),
    onSuccess: () => {
      toast.success('Subscription cancelled successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel subscription');
    }
  });

  const handleSubscribe = (planType) => {
    // For now, this is a placeholder - just show a message
    toast.success(`Selected ${plans[planType]?.name || planType} package. Payment integration coming soon!`);
    // TODO: Uncomment when ready to integrate payment
    // checkoutMutation.mutate(planType);
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel your subscription?')) {
      cancelMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  // Show error message if there's a permission error, but still show plans
  const hasPermissionError = error?.response?.status === 403;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[var(--text-primary)]">Subscription Packages</h1>
        <p className="text-[var(--text-secondary)] mt-2">Choose a subscription package that works for you</p>
      </div>

      {hasPermissionError && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            Note: Subscription packages are available. Payment processing will be enabled soon.
          </p>
        </div>
      )}

      {/* Current Subscription Status */}
      {subscription?.hasPaidSubscription ? (
        <div className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-[var(--text-primary)] group-hover:text-[var(--text-primary)] transition-colors">Active Subscription</h2>
              <p className="text-[var(--text-secondary)] mt-1 group-hover:text-[var(--text-secondary)] transition-colors">
                ${parseFloat(subscription.monthlyAmount || 0).toFixed(2)}/month
              </p>
            </div>
            <div className="flex items-center gap-2 text-green-600 group-hover:text-green-700 transition-colors">
              <Check className="w-5 h-5" />
              <span className="font-medium">Active</span>
            </div>
          </div>

          {subscription.subscriptionStartDate && (
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Started:</span>
                <span className="text-[var(--text-primary)]">
                  {format(new Date(subscription.subscriptionStartDate), 'MMM d, yyyy')}
                </span>
              </div>
              {subscription.subscriptionEndDate && (
                <div className="flex justify-between">
                  <span className="text-[var(--text-secondary)]">Renews:</span>
                  <span className="text-[var(--text-primary)]">
                    {format(new Date(subscription.subscriptionEndDate), 'MMM d, yyyy')}
                  </span>
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
            className="btn btn-outline text-red-600 hover:bg-red-50 mt-4 shadow-sm hover:shadow-md transition-all duration-300"
          >
            {cancelMutation.isPending ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Cancelling...
              </>
            ) : (
              <>
                <X className="w-4 h-4 mr-2" />
                Cancel Subscription
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <div className="text-center py-8">
            <CreditCard className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">No Active Subscription</h2>
            <p className="text-[var(--text-secondary)]">Choose a plan to get started</p>
          </div>
        </div>
      )}

      {/* Available Packages */}
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">Choose Your Package</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(plans).map(([planType, plan]) => (
            <div
              key={planType}
              className={`card relative group transition-all hover:shadow-xl transform hover:-translate-y-1 border-2 ${subscription?.monthlyAmount === plan.price ? 'ring-2 ring-primary-600 border-primary-300' : 'border-transparent hover:border-[var(--border-subtle)]'} ${plan.popular ? 'ring-2 ring-accent-500 border-accent-200 scale-105' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-accent-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-[var(--text-primary)]">${plan.price}</span>
                  <span className="text-[var(--text-secondary)] text-lg">/month</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8 min-h-[180px]">
                {plan.features && plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[var(--text-secondary)]">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {subscription?.hasPaidSubscription && subscription?.monthlyAmount === plan.price ? (
                <button disabled className="btn btn-outline w-full opacity-50 cursor-not-allowed shadow-sm">
                  Current Plan
                </button>
              ) : (
                <button
                  onClick={() => handleSubscribe(planType)}
                  disabled={checkoutMutation.isPending}
                  className={`btn w-full text-lg py-3 font-semibold transition-all duration-300 ${
                    plan.popular 
                      ? 'bg-accent-500 hover:bg-accent-600 text-white border-accent-500 shadow-lg shadow-accent-200 hover:shadow-xl' 
                      : 'btn-primary shadow-md hover:shadow-lg'
                  }`}
                >
                  {checkoutMutation.isPending ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Select Package
                    </>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

